import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Xmark } from '@peacecover/icon';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { useSceneAllParagraphsWithIndexedDB } from './hooks/useSceneAllParagraphsWithIndexedDB';
import { useUpdateReadingProgress } from '../../domains/scene/ui/hooks/useUpdateReadingProgress';
import { useGetSceneDetail } from '../../domains/scene/ui/hooks/useGetSceneDetail';
import { usePageIndexProgress } from './hooks/usePageIndexProgress';
import { useReadingProgressFromPage } from './hooks/useReadingProgressFromPage';
import { useEpisodePagesMeasurement } from './hooks/useEpisodePagesMeasurement';
import { flattenEpisodesToPages } from './utils/flattenEpisodesToPages';
import { getPageIndexForLocator } from './utils/getPageIndexForLocator';
import { getReservedViewportHeight } from './utils/viewportUtils';
import EpisodePageSlider from './components/EpisodePageSlider';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import MarqueeTitle from '../components/MarqueeTitle';
import { SCENE_POST_MESSAGE_TYPES } from './constants/postMessageTypes';

const OVERLAY_MIN_TIME_MS = 400;

const SceneOriginalEpisodesPage = () => {
  const { t } = useTranslation();
  const handleHistoryBack = useHandleHistoryBack();
  const { sceneId } = useParams();
  const [searchParams] = useSearchParams();
  const contentAreaRef = useRef<HTMLDivElement | null>(null);
  const [contentAreaHeight, setContentAreaHeight] = useState(0);

  useEffect(() => {
    const notifyOpenerToRefetch = () => {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: SCENE_POST_MESSAGE_TYPES.INVALIDATE_SCENE_SUBSCRIPTIONS },
          window.location.origin
        );
      }
    };

    const handleBeforeUnload = () => notifyOpenerToRefetch();

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      notifyOpenerToRefetch();
    };
  }, []);

  useEffect(() => {
    const el = contentAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContentAreaHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const targetEpisodeId = searchParams.get('episodeId')?.trim() || undefined;
  const cursorParam = searchParams.get('cursor')?.trim();
  const charOffsetParam = searchParams.get('charOffset')?.trim();
  const targetParagraphIndex =
    cursorParam !== undefined && cursorParam !== ''
      ? (() => {
          const n = Number.parseInt(cursorParam, 10);
          return Number.isNaN(n) ? null : n;
        })()
      : null;
  const targetCharOffset =
    charOffsetParam !== undefined && charOffsetParam !== ''
      ? (() => {
          const n = Number.parseInt(charOffsetParam, 10);
          return Number.isNaN(n) ? null : n;
        })()
      : null;

  const { data, isLoading } = useSceneAllParagraphsWithIndexedDB(sceneId ?? '');

  const { data: sceneDetail } = useGetSceneDetail(sceneId ?? '');

  const episodes = useMemo(() => data?.sceneEpisodes ?? [], [data?.sceneEpisodes]);
  const { episodePageCounts, isMeasuring } = useEpisodePagesMeasurement(
    episodes,
    sceneId ?? undefined,
    contentAreaHeight
  );
  const pages = useMemo(
    () => flattenEpisodesToPages(episodes, episodePageCounts),
    [episodes, episodePageCounts]
  );
  const targetPageIndex = useMemo(() => {
    if (!targetEpisodeId) return null;
    const target = pages.find((page) => page.episode.sceneEpisodeId === targetEpisodeId);
    return target?.globalPageIndex ?? null;
  }, [pages, targetEpisodeId]);
  const totalEpisodes = data?.sceneEpisodes?.length ?? episodes.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [overlayMinTimePassed, setOverlayMinTimePassed] = useState(false);
  const currentPageRef = useRef<HTMLDivElement | null>(null);
  const { mutate: updateReadingProgress } = useUpdateReadingProgress();

  useEffect(() => {
    if (!targetEpisodeId) return;
    setOverlayMinTimePassed(false);
    const t = setTimeout(() => setOverlayMinTimePassed(true), OVERLAY_MIN_TIME_MS);
    return () => clearTimeout(t);
  }, [targetEpisodeId]);

  useEffect(() => {
    if (isLoading || isMeasuring || pages.length === 0 || !targetEpisodeId) return;

    const resolveTargetIndex = (): number | null => {
      if (targetParagraphIndex != null && targetCharOffset != null && sceneId && targetEpisodeId) {
        const targetEpisode = episodes.find((ep) => ep.sceneEpisodeId === targetEpisodeId);
        const paragraphs = targetEpisode?.sceneEpisodeParagraphs ?? [];

        if (paragraphs.length > 0) {
          const pageWidth = typeof window !== 'undefined' ? window.innerWidth : 360;
          const rawHeight =
            contentAreaHeight > 0
              ? contentAreaHeight
              : (() => {
                  const fullHeight = typeof window !== 'undefined' ? window.innerHeight : 600;
                  const reserved = getReservedViewportHeight();
                  return Math.max(fullHeight - reserved, fullHeight * 0.6);
                })();
          const effectiveHeight = Math.max(1, rawHeight);

          const pageIndexWithinEpisode = getPageIndexForLocator(
            paragraphs,
            targetEpisodeId,
            targetParagraphIndex,
            targetCharOffset,
            pageWidth,
            effectiveHeight
          );

          const targetPage = pages.find(
            (p) =>
              p.episode.sceneEpisodeId === targetEpisodeId && p.pageIndex === pageIndexWithinEpisode
          );
          return targetPage?.globalPageIndex ?? null;
        }
      }
      return targetPageIndex;
    };

    const index = resolveTargetIndex();
    if (index != null) {
      setCurrentIndex(index);
    }
  }, [
    isLoading,
    isMeasuring,
    pages,
    episodes,
    targetEpisodeId,
    targetPageIndex,
    targetParagraphIndex,
    targetCharOffset,
    sceneId,
    contentAreaHeight
  ]);

  const { progressPercent, currentEpisodeNumber, canScrollLeft, canScrollRight } =
    usePageIndexProgress({
      currentIndex,
      pages,
      episodes
    });

  useReadingProgressFromPage({
    currentPageRef,
    sceneId: sceneId ?? undefined,
    updateReadingProgress,
    currentIndex,
    currentPage: pages[currentIndex] ?? null
  });

  const handlePrevPage = useCallback(() => {
    if (!canScrollLeft) return;
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, [canScrollLeft]);

  const handleNextPage = useCallback(() => {
    if (!canScrollRight) return;
    setCurrentIndex((i) => Math.min(pages.length - 1, i + 1));
  }, [canScrollRight, pages.length]);

  const setCurrentPageRef = useCallback((el: HTMLDivElement | null) => {
    currentPageRef.current = el;
  }, []);


  const showOverlay = targetEpisodeId && (isLoading || isMeasuring || !overlayMinTimePassed);

  return (
    <div className="fixed inset-0 flex flex-col gap-[24px] overflow-hidden overscroll-none pt-[env(safe-area-inset-top)] bg-background-light dark:bg-background-dark">
      <header className="relative z-50 flex items-center justify-between px-[16px] py-[10px] bg-background-light dark:bg-background-dark">
        <button type="button" onClick={handleHistoryBack}>
          <Xmark className="text-text-main-lv1" />
        </button>
        <MarqueeTitle title={sceneDetail?.title ?? ''} textClassName="text-text-main-lv1" />
      </header>

      <div
        ref={contentAreaRef}
        className="relative flex-1 min-h-0 w-screen overflow-hidden pb-[calc(env(safe-area-inset-bottom)+74px)]"
      >
        {showOverlay && (
          <div
            className="absolute inset-0 z-40 flex items-center justify-center bg-background-light dark:bg-background-dark"
            aria-live="polite"
          >
            <GradientLoadingSpinner size={36} />
          </div>
        )}
        {isLoading || isMeasuring ? (
          <div className="relative flex h-full w-full min-w-0 items-center justify-center overflow-hidden">
            <GradientLoadingSpinner size={36} />
          </div>
        ) : pages.length === 0 ? (
          <div className="relative flex h-full w-full min-w-0 flex-row items-stretch overflow-x-auto overflow-y-hidden hiddenScrollBar">
            <p className="min-w-full flex-shrink-0 px-[17px] py-[16px] text-center text-[14px] text-text-main-lv3">
              {t('SceneOriginalEpisodes.emptyTitle')}
            </p>
          </div>
        ) : (
          <EpisodePageSlider
            sceneId={sceneId ?? ''}
            pages={pages}
            currentIndex={currentIndex}
            onIndexChange={setCurrentIndex}
            currentPageRef={setCurrentPageRef}
          />
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-50 rounded-t-[20px] flex gap-[34px] items-center justify-center pt-[16px] pb-[calc(env(safe-area-inset-bottom)+10px)] bg-pure-white dark:bg-pure-black shadow-[0_1px_5.8px_0_rgba(0,0,0,0.12)]">
        <button
          type="button"
          onClick={handlePrevPage}
          disabled={!canScrollLeft}
          className="flex-shrink-0 p-[6px] rounded-full bg-separator1 disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation"
        >
          <ArrowLeft size={24} className="text-text-main-lv4" />
        </button>
        <div className="flex flex-col items-center gap-[8px] justify-center">
          <div className="relative w-[143px]">
            <div className="w-full h-[6px] bg-black-opacity-200 dark:bg-white-opacity-200 rounded-[20px]" />
            <div
              className="absolute top-0 left-0 h-[6px] overflow-hidden rounded-[20px]"
              style={{ width: `${progressPercent}%` }}
            >
              <div
                className="h-full w-[143px] rounded-[20px] bg-[linear-gradient(90deg,#41F7A2_0%,#5C3DFA_100%)]"
                aria-hidden
              />
            </div>
          </div>
          <div className="flex items-center">
            <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3 flex-1 text-center min-w-0">
              {totalEpisodes > 0
                ? `${Math.min(currentEpisodeNumber, totalEpisodes)} / ${totalEpisodes} ${t('SceneOriginalEpisodes.episode')}`
                : '-'}
            </p>
            <div className="w-[1px] h-[10px] bg-[#D9D9D9] ml-[7px] mr-[10px]" />
            <p className="text-[12px] leading-[14px] text-text-main-lv3 font-semibold whitespace-nowrap">
              {progressPercent}%
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleNextPage}
          disabled={!canScrollRight}
          className="flex-shrink-0 p-[6px] rounded-full bg-separator1 disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation"
        >
          <ArrowRight size={24} className="text-text-main-lv4" />
        </button>
      </div>
    </div>
  );
};

export default SceneOriginalEpisodesPage;
